<template>
  <aside aria-hidden="true">
    <div
      ref="bar"
      class="sticky-bar"
      :class="{ '-sticky': sticky, '-bottom': bottom }"
    >
      <div class="container">
        <div class="row align-items-center">
          <div class="sticky-bar__text col">
            <p class="text-left -upper font-bold sticky-bar__text--title mb-0" v-text="item.name"></p>
          </div>
          <div class="sticky-bar__buttons col d-flex align-items-center justify-content-end">
            <template
              v-if="ecommerceActivated && !item.product.is_icon && getProductPrice(item.product) !== 0"
            >
              <button
                tabindex="-1"
                type="button"
                class="btn"
                v-if="isInStock && !item.isPreorder"
                @click="clickAddToCart(item.product)"
              >
                {{ $t("Add to cart") }}
              </button>
              <button
                tabindex="-1"
                type="button"
                class="btn"
                v-else-if="isInStock && item.isPreorder"
                @click="clickAddToCart(item.product)"
              >
                {{ $t("Preorder") }}
              </button>
              <button
                tabindex="-1"
                type="button"
                class="btn"
                v-else-if="
                  item.product &&
                    !isInStock &&
                    item.product.is_future_product
                "
                aria-controls="modal-availability"
                :aria-expanded="availableModal ? 'true' : 'false'"
                @click="clickAlertAvailability"
                :aria-label="
                  $t('Coming soon - Notify me') + ' ' + $t('(opens dialog)')
                "
              >
                {{ $t('Coming soon - Notify me') }}
              </button>
              <button
                tabindex="-1"
                type="button"
                class="btn"
                v-else-if="(item.product.isShoppable && !isInStock && item.product.availability_alert) || (!isSimpleProduct && strapSize !== null)"
                @click="clickAlertAvailability"
              >
                {{ $t("Notify me when available") }}
              </button>
            </template>
            <template v-else-if="item.product && item.product.is_icon">
              <router-link
                v-if="item.iconCtaLabel && item.iconCtaUrl"
                type="button"
                class="btn"
                :to="localizedUrl + item.iconCtaUrl"
                v-text="item.iconCtaLabel"
              ></router-link>
            </template>
            <router-link
              v-if="!item.onlyAvailableOnline && !item.product.is_icon"
              tabindex="-1"
              :to="localizedUrl + '/store-locator'"
              class="btn -stroke d-none d-md-flex ml-3"
              :class="{ '-ecom': ecommerceActivated }"
              v-text="$t('Shop in store')"
              @click.native="storeLocatorEvent({ productName: item.name, siteLocation: 'PDP' })"
            >
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>

<script>
import { currentStoreView } from '@vue-storefront/core/lib/multistore'
import { mapActions, mapGetters } from 'vuex'
import { ReadyHook } from 'vue-asyncready'
import { getProductPrice, storeLocatorEvent } from '../utilities'
import { throttle } from 'throttle-debounce'

export default {
  props: ['item', 'size', 'straps', 'isInStock', 'selectedVariantSku', 'strapSize', 'isSimpleProduct', 'availableModal'],
  name: 'StickyProductBar',
  mixins: [ReadyHook],
  data() {
    return {
      autoReady: false,
      sticky: false,
      bottom: false,
      ecommerceActivated: undefined,
      throttledScrollHandler: null
    }
  },
  mounted() {
    document.querySelector('main').classList.add('-product')
    this.throttledScrollHandler = throttle(50, this.isSticky)
    window.addEventListener('scroll', this.throttledScrollHandler)
    this.$ready()
    this.isSticky()
    this.ecommerceActivated = this.$store.state['zenith-ui'].ecommerce
    setTimeout(() => {
      window.dispatchEvent(new Event('scroll'))
      window.dispatchEvent(new Event('resize'))
    }, 100)
  },
  methods: {
    getProductPrice: getProductPrice,
    ...mapActions('cart', { addToCart: 'addItem' }),
    ...mapActions('feedback', ['show']),
    ...mapGetters('user', ['isLoggedIn']),
    storeLocatorEvent,
    isSticky() {
      if (this.$el) {
        const elClientRectTop = this.$el.getBoundingClientRect().top
        this.sticky = elClientRectTop <= 0
      }
      this.bottom =
        window.scrollY + window.innerHeight >
        document.querySelector('footer.footer').offsetTop
      this.topDistance()
    },
    clickAddToCart(product) {
      this.$emit('add-to-cart-click')
    },
    clickAlertAvailability() {
      this.$emit('alert-availability')
    },
    alertIcons() {
      this.$emit('alertIcons')
    },
    topDistance() {
      if (document) {
        let topDistance = 0
        const header = document.querySelector('.header-wrapper')
        if (header && !header.classList.contains('-scroll-down')) {
          const headerStyle = window.getComputedStyle(header)
          const height = parseInt(headerStyle.getPropertyValue('--zenith-header-height'))
          const top = parseInt(headerStyle.getPropertyValue('top'))
          if (height) {
            topDistance = height + top
          }
        }
        this.$el.style.setProperty('--top-distance', topDistance + 'px')
        return topDistance
      }
    }
  },
  watch: {
    bottom() {
      if (this.bottom) {
        document.querySelector('main').classList.add('-relative')
      } else {
        document.querySelector('main').classList.remove('-relative')
      }
    }
  },
  computed: {
    accountActivated() {
      const { account } = currentStoreView()
      return account !== false
    },
    localizedUrl() {
      return currentStoreView().url || ''
    }
  },
  destroyed: function () {
    document.querySelector('main').classList.remove('-product')
    window.removeEventListener('scroll', this.throttledScrollHandler)
  }
}
</script>

<style lang="scss" scoped>
@import "~theme/css/core";

aside {
  .sticky-bar {
    z-index: 80;
    padding-block: 1rem;
    position: fixed;
    top: var(--top-distance, 0px);
    left: 0;
    width: 100%;
    transition: all 0.2s ease-in-out;
    background: var(--zenith-sticky-product-bar-background);
    opacity: 0;
    pointer-events: none;
    overflow: hidden;

    @include bp(md) {
      padding-block: 3rem;
    }

    &.-sticky {
      opacity: 1;
      pointer-events: all;
    }

    &.-bottom {
      position: absolute;
    }

    &__text {
      &--title {
        font-size: 1.2rem;

        @include bp(md) {
          font-size: 1.8rem;
        }
      }
    }
    &__buttons {
      .btn {
        font-size: 1.2rem;
        min-height: auto;
        padding-block: 1rem;
      }
    }
  }
}
</style>
